import React, { Fragment } from 'react'
import { Box, TextField, Chip, useTheme, useMediaQuery } from '@mui/material'
import { CollapsibleInputCard } from '../../../components/_WMS/CollapsibleInputCard'
import { CameraScanButton } from '../../../components/_WMS/CameraScan'
import { useScanKeyboard } from '../../../hooks/useScanKeyboard'
import { LoadingButton } from '../../../components/_EOS/Button/LoadingButton/LoadingButton'
import { formatExpirationDate } from '../../../utils/dateHelper'
import { toDunPacks } from '../utils/dunUtils'
import { PositionAttributeAlert } from '../../../components/UI/PositionAttributeAlert/PositionAttributeAlert'

interface OriginPositionSectionProps {
  originInputRef: React.RefObject<HTMLInputElement>
  localOriginPosition: string
  setLocalOriginPosition: (value: string) => void
  originValidated: boolean
  originValidationFailed: boolean
  isValidating: boolean
  error?: string
  handleValidateOrigin: (scannedValue?: string) => void
  handleEditOrigin: () => void
  sharedState: {
    posicionOrigen: string
    selectedBatch?: {
      batchNumber: string
      expirationDate?: string | null
    }
    availableStock?: number
    isDun?: boolean
    dunQty?: number
    typePackageName?: string
  }
}

export const OriginPositionSection: React.FC<OriginPositionSectionProps> = ({
  originInputRef,
  localOriginPosition,
  setLocalOriginPosition,
  originValidated,
  originValidationFailed,
  isValidating,
  error,
  handleValidateOrigin,
  handleEditOrigin,
  sharedState
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // En modo camara el teclado virtual no se levanta salvo que toquen el input.
  const { autoFocus, isCameraCapture } = useScanKeyboard()

  return (
    <Box mb={1}>
      <CollapsibleInputCard
        title="Posición de Origen"
        isValidated={originValidated}
        validationFailed={originValidationFailed}
        onEdit={handleEditOrigin}
        isMobile={isMobile}
        expandedContent={
          <Fragment>
          {/* En modo camara siempre columna: el boton de camara va entre el
              input y "Validar", y en fila los tres no entrarian. */}
          <Box display="flex" flexDirection={isMobile || isCameraCapture ? 'column' : 'row'}>
            <TextField
              inputRef={originInputRef}
              label="Código de Posición"
              variant="outlined"
              fullWidth
              value={localOriginPosition}
              onChange={(e) => setLocalOriginPosition(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleValidateOrigin()
                }
              }}
              error={originValidationFailed}
              style={{
                marginRight: isMobile || isCameraCapture ? 0 : theme.spacing(1),
                marginBottom: isMobile || isCameraCapture ? theme.spacing(2) : 0
              }}
            />

            {/* Entre el input y "Validar": es una alternativa para llenar el
                campo, no una accion sobre lo ya ingresado. */}
            <Box mb={1.5} width="100%">
              <CameraScanButton
                inputRef={originInputRef}
                onScan={(value) => {
                  setLocalOriginPosition(value)
                  handleValidateOrigin(value)
                }}
                onCancel={() => autoFocus && setTimeout(() => originInputRef.current?.focus(), 0)}
                disabled={isValidating}
                dialogTitle='Escanear posición de origen'
                helperText='Centra el código de la posición dentro del recuadro'
              />
            </Box>

            <LoadingButton
              loading={isValidating}
              variant="contained"
              color="primary"
              // Envuelto a proposito: pasarlo directo mandaria el MouseEvent
              // como scannedValue.
              onClick={() => handleValidateOrigin()}
              disabled={!localOriginPosition.trim()}
              fullWidth={isMobile || isCameraCapture}
              style={{ minWidth: isMobile ? 'auto' : '100px' }}
            >
              Validar
            </LoadingButton>
          </Box>
          {error && <PositionAttributeAlert message={error} variant='body2' />}
          </Fragment>
        }
        collapsedSummary={`Posición Origen: ${sharedState.posicionOrigen}`}
        collapsedChips={
          <Box display="flex" flexWrap="wrap">
            {sharedState.selectedBatch ? (
              <Fragment>
                <Chip
                  label={`Lote: ${sharedState.selectedBatch.batchNumber}`}
                  color="primary"
                  size="small"
                  style={{
                    marginRight: sharedState.selectedBatch.expirationDate ? theme.spacing(1) : 0
                  }}
                />
                {sharedState.selectedBatch.expirationDate && (
                  <Chip
                    label={`Vence: ${formatExpirationDate(sharedState.selectedBatch.expirationDate, 'dd-MM-yyyy')}`}
                    style={{ backgroundColor: theme.palette.warning.light, color: theme.palette.warning.contrastText }}
                    size="small"
                  />
                )}
              </Fragment>
            ) : (
              <Chip
                // Con DUN el stock de la posición se muestra en cajas completas:
                // Ejemplo: 104 unidades con caja de 6 son 17 cajas (el resto no es reubicable).
                label={
                  sharedState.isDun
                    ? `Stock: ${toDunPacks(sharedState.availableStock || 0, sharedState.dunQty)} ${sharedState.typePackageName || 'Piezas agrupadas'}`
                    : `Stock: ${sharedState.availableStock} unidades`
                }
                color="primary"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        }
      />
    </Box>
  )
}